import * as zero from "@rocicorp/zero"
import * as z from "zod/mini"
import type { AuthData } from "./auth.ts"
import { builder, type schema } from "./schema.ts"

/** Gets account with memberships and subscriptions for channel access */
export const accountWithMemberships = zero.syncedQueryWithContext(
  "accountWithMemberships",
  z.tuple([
    z.object({
      channelId: z.string(),
      organizationId: z.union([z.string(), z.undefined()]),
      repositoryId: z.string(),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return builder.account
      .where("id", "=", authData?.id ?? "")
      .related("channel_memberships", (memberships) =>
        memberships.where("channel_id", "=", args.channelId).one(),
      )
      .related("channel_subscriptions")
      .related("organization_memberships", (memberships) =>
        memberships
          .where("organization_id", "=", args.organizationId ?? "none")
          .one(),
      )
      .related("repository_memberships", (memberships) =>
        memberships.where("repository_id", "=", args.repositoryId).one(),
      )
      .where((eb) =>
        eb.and(
          accountIsLoggedIn(authData, eb),
          eb.cmp("id", "=", authData?.id ?? ""),
        ),
      )
      .one()
  },
)

/** Gets account with channel and thread subscriptions for repository */
export const accountWithRepositorySubscriptions = zero.syncedQueryWithContext(
  "accountWithRepositorySubscriptions",
  z.tuple([
    z.object({
      repositoryId: z.string(),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return builder.account
      .where("id", "=", authData?.id ?? "")
      .related("channel_subscriptions", (subscriptions) =>
        subscriptions
          .where("account_id", "=", authData?.id ?? "")
          .whereExists("channel", (channel) =>
            channel.where("repository_id", "=", args.repositoryId),
          ),
      )
      .related("thread_subscriptions", (subscriptions) =>
        subscriptions
          .where("account_id", "=", authData?.id ?? "")
          .whereExists("thread", (thread) =>
            thread.whereExists("message", (message) =>
              message.whereExists("channel", (channel) =>
                channel.where("repository_id", "=", args.repositoryId),
              ),
            ),
          )
          .related("thread"),
      )
      .where((eb) =>
        eb.and(
          accountIsLoggedIn(authData, eb),
          eb.cmp("id", "=", authData?.id ?? ""),
        ),
      )
      .one()
  },
)

/** Gets channel for channel ID */
export const channelWithPermissions = zero.syncedQueryWithContext(
  "channelWithPermissions",
  z.tuple([z.object({ id: z.string() })]),
  (authData: AuthData | null | undefined, args) => {
    return builder.channel
      .where("id", "=", args.id)
      .related("permissions")
      .related("repository", (repository) =>
        repository
          .related("account")
          .related("channels", (channels) =>
            channels
              .related("messages", (messages) =>
                messages
                  .where("thread_id", "IS", null)
                  .orderBy("inserted_at", "desc")
                  .limit(1)
                  .one(),
              )
              .where((eb) => canAccessChannel(authData, eb)),
          )
          .related("organization")
          .related("installation"),
      )
      .where((eb) => canAccessChannel(authData, eb))
      .one()
  },
)

/** Gets current authenticated user account */
export const me = zero.syncedQueryWithContext(
  "me",
  z.tuple([z.object({})]),
  (authData: AuthData | null | undefined, _args) => {
    return builder.account.where("id", "=", authData?.id ?? "").one()
  },
)

/** Gets messages for channel with optional thread filtering */
export const messagesForChannel = zero.syncedQueryWithContext(
  "messagesForChannel",
  z.tuple([
    z.object({
      channelId: z.string(),
      direction: z.optional(
        z.union([z.literal("backward"), z.literal("forward")]),
      ),
      limit: z.optional(z.union([z.number(), z.undefined()])),
      messageId: z.optional(z.union([z.string(), z.undefined()])),
      start: z.optional(
        z.object({
          id: z.string(),
          inserted_at: z.number(),
          updated_at: z.number(),
        }),
      ),
      threadId: z.optional(z.union([z.string(), z.undefined()])),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    const orderByDirection = args.direction === "backward" ? "desc" : "asc"
    let query = builder.message
      .where("channel_id", args.channelId)
      .orderBy("inserted_at", orderByDirection)
      .orderBy("id", orderByDirection)
      .related("account")
      .related("attachments")
      .related("reactions")

    const messageId = args.messageId
    const isThread = Boolean(messageId)
    if (isThread && messageId)
      query = query.where((eb) =>
        eb.or(
          eb.cmp("thread_id", "=", args.threadId ?? ""),
          eb.cmp("id", "=", messageId),
        ),
      )
    else query = query.where("thread_id", "IS", null).related("thread")

    query = query.where((eb) => canAccessMessage(authData, eb))

    if (args.start) query = query.start(args.start)
    if (args.limit) query = query.limit(args.limit)
    return query
  },
)

/** Gets repositories for account/organization  */
export const repositoriesForAccount = zero.syncedQueryWithContext(
  "repositoriesForAccount",
  z.tuple([
    z.object({
      id: z.string(),
      type: z.union([z.literal("account"), z.literal("organization")]),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return builder.repository
      .where((q) =>
        q.cmp(
          args.type === "account" ? "account_id" : "organization_id",
          args.id,
        ),
      )
      .where((eb) => canAccessRepository(authData, eb))
  },
)

/** Gets repository with GitHub reference (issue/PR) for rendering markdown links */
export const repositoryWithGitHubReference = zero.syncedQueryWithContext(
  "repositoryWithGitHubReference",
  z.tuple([
    z.object({
      number: z.number(),
      owner: z.union([z.string(), z.undefined()]),
      repo: z.union([z.string(), z.undefined()]),
      repositoryId: z.string(),
      type: z.union([
        z.literal("issues"),
        z.literal("pull"),
        z.literal("unknown"),
      ]),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    const baseQuery = builder.repository
      .where((eb) =>
        args.owner && args.repo
          ? eb.and(
              eb.or(
                eb.exists("account", (eb) =>
                  eb.where("login", "=", args.owner ?? ""),
                ),
                eb.exists("organization", (eb) =>
                  eb.where("login", "=", args.owner ?? ""),
                ),
              ),
              eb.cmp("name", "=", args.repo ?? ""),
            )
          : eb.cmp("id", "=", args.repositoryId),
      )
      .related("account")
      .related("organization")
      .where((eb) => canAccessRepository(authData, eb))

    const allQuery = baseQuery
      .related("issues", (issues) =>
        issues.where("number", "=", args.number).one(),
      )
      .related("pullRequests", (pullRequests) =>
        pullRequests.where("number", "=", args.number).one(),
      )
      .one()

    const query = (() => {
      if (args.type === "unknown") return allQuery
      if (args.type === "issues")
        return baseQuery
          .related("issues", (issues) =>
            issues.where("number", "=", args.number).one(),
          )
          .one()
      return baseQuery
        .related("pullRequests", (pullRequests) =>
          pullRequests.where("number", "=", args.number).one(),
        )
        .one()
    })()
    return query as typeof allQuery
  },
)

/** Gets repository with channels and related data */
export const repositoryWithChannels = zero.syncedQueryWithContext(
  "repositoryWithChannels",
  z.tuple([
    z.object({
      repositoryId: z.string(),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return builder.repository
      .where("id", "=", args.repositoryId)
      .related("account")
      .related("channels", (channels) =>
        channels
          .orderBy("name", "asc")
          .related("messages", (messages) =>
            messages
              .where("thread_id", "IS", null)
              .orderBy("inserted_at", "desc")
              .limit(1)
              .one(),
          )
          .where((eb) => canAccessChannel(authData, eb)),
      )
      .related("installation")
      .related("organization")
      .where((eb) => canAccessRepository(authData, eb))
      .one()
  },
)

/** Gets repository with owner (account/organization) information */
export const repositoryWithOwner = zero.syncedQueryWithContext(
  "repositoryWithOwner",
  z.tuple([
    z.object({
      repositoryId: z.string(),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return builder.repository
      .where("id", "=", args.repositoryId)
      .related("account")
      .related("organization")
      .where((eb) => canAccessRepository(authData, eb))
      .one()
  },
)

/** Gets thread subscription for account and thread */
export const threadAccounts = zero.syncedQueryWithContext(
  "threadAccounts",
  z.tuple([
    z.object({
      accountCount: z.union([z.number(), z.undefined()]),
      threadId: z.string(),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return (
      builder.account
        // get all accounts participating in thread
        .whereExists("messages", (q) =>
          q
            .where("thread_id", args.threadId)
            .where((eb) => canAccessMessage(authData, eb)),
        )
        // and their first message
        .related("messages", (q) =>
          q
            .where("thread_id", args.threadId)
            .orderBy("inserted_at", "asc")
            .limit(1),
        )
        .limit(args.accountCount ?? 0)
    )
  },
)

/** Gets thread subscription for account and thread */
export const threadSubscription = zero.syncedQueryWithContext(
  "threadSubscription",
  z.tuple([
    z.object({
      threadId: z.string(),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return builder.thread_subscription
      .where("account_id", "=", authData?.id ?? "")
      .where("thread_id", "=", args.threadId)
      .where((eb) =>
        eb.and(
          accountIsLoggedIn(authData, eb),
          eb.cmp("account_id", "=", authData?.id ?? ""),
        ),
      )
      .one()
  },
)

/** Gets threads with subscriptions for account in repository */
export const threadsWithSubscriptions = zero.syncedQueryWithContext(
  "threadsWithSubscriptions",
  z.tuple([
    z.object({
      repositoryId: z.string(),
    }),
  ]),
  (authData: AuthData | null | undefined, args) => {
    return builder.thread
      .where("message_count", ">", 0)
      .whereExists(
        "subscriptions",
        (subscriptions) =>
          subscriptions.where("account_id", "=", authData?.id ?? ""),
        { flip: true },
      )
      .whereExists("message", (message) =>
        message
          .whereExists("channel", (channel) =>
            channel.where("repository_id", "=", args.repositoryId),
          )
          .where((eb) => canAccessMessage(authData, eb)),
      )
      .related("message", (message) =>
        message
          .related("account")
          .related("channel")
          .related("attachments")
          .related("reactions"),
      )
      .related("messages", (messages) =>
        messages
          .related("account")
          .related("attachments")
          .related("reactions")
          .orderBy("inserted_at", "desc")
          .limit(3),
      )
      .where((eb) =>
        eb.and(
          accountIsLoggedIn(authData, eb),
          eb.exists("subscriptions", (q) =>
            q.where("account_id", "=", authData?.id ?? ""),
          ),
        ),
      )
      .orderBy("last_message_at", "desc")
  },
)

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

// TODO: Figure out what to do with these (used for `tests mutators.test.ts`)

export const getChannel = zero.syncedQueryWithContext(
  "getChannel",
  z.tuple([z.object({ id: z.string() })]),
  (authData: AuthData | null | undefined, args) => {
    return builder.channel
      .where("id", "=", args.id)
      .where((eb) => canAccessChannel(authData, eb))
      .related("permissions")
      .related("members")
      .related("subscriptions")
      .related("repository", (repository) =>
        repository
          .related("members")
          .related("organization", (organization) =>
            organization.related("members"),
          ),
      )
      .one()
  },
)

export const getMessage = zero.syncedQueryWithContext(
  "getMessage",
  z.tuple([z.object({ id: z.string() })]),
  (authData: AuthData | null | undefined, args) => {
    return builder.message
      .where("id", "=", args.id)
      .where((eb) => canAccessMessage(authData, eb))
      .related("channel", (channel) =>
        channel.related("permissions").related("repository"),
      )
      .related("reactions")
      .related("thread")
      .one()
  },
)

export const getThreadSubscription = zero.syncedQueryWithContext(
  "getThreadSubscription",
  z.tuple([z.object({ id: z.string() })]),
  (authData: AuthData | null | undefined, args) => {
    return builder.thread_subscription
      .where("id", args.id)
      .where("account_id", authData?.id ?? "")
      .one()
  },
)

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

function definePermission<table extends keyof schema["tables"]>(
  callback: zero.PermissionRule<AuthData | null | undefined, schema, table>,
) {
  return callback
}

const accountIsLoggedIn = definePermission((auth, eb) =>
  eb.cmpLit(auth?.id ?? "", "!=", ""),
)

const accountIsAdmin = definePermission((auth, eb) =>
  eb.and(accountIsLoggedIn(auth, eb), eb.cmpLit(auth?.role ?? "", "=", "crew")),
)

const accountIsOwner = definePermission<
  | "channel_member"
  | "installation"
  | "message"
  | "organization_member"
  | "reaction"
  | "repository"
  | "repository_member"
>((auth, eb) =>
  eb.and(
    accountIsLoggedIn(auth, eb),
    eb.cmp("account_id", "=", auth?.id ?? ""),
  ),
)

const accountIsRepositoryMember = definePermission<"repository">((auth, eb) =>
  eb.and(
    accountIsLoggedIn(auth, eb),
    eb.exists("members", (q) =>
      q.where((eb) => eb.cmp("account_id", "=", auth?.id ?? "")),
    ),
  ),
)

const accountIsOrganizationMember = definePermission<
  "installation" | "repository"
>((auth, eb) =>
  eb.and(
    accountIsLoggedIn(auth, eb),
    eb.cmp("organization_id", "IS NOT", null),
    eb.exists("organization", (q) =>
      q.whereExists("members", (q) =>
        q.where((eb) => eb.cmp("account_id", "=", auth?.id ?? "")),
      ),
    ),
  ),
)

const installationIsActive = definePermission<"installation">((_auth, eb) =>
  eb.and(eb.cmp("suspended_at", "IS", null), eb.cmp("deleted_at", "IS", null)),
)

const repositoryIsActive = definePermission<"repository">((auth, eb) =>
  eb.exists("installation_repository", (q) =>
    q.where((eb) =>
      eb.and(
        eb.cmp("suspended_at", "IS", null),
        eb.exists("installation", (q) =>
          q.where((eb) => installationIsActive(auth, eb)),
        ),
      ),
    ),
  ),
)

const canAccessRepository = definePermission<"repository">((auth, eb) =>
  eb.and(
    repositoryIsActive(auth, eb),
    eb.or(
      eb.cmp("private", "=", false),
      eb.and(
        eb.cmp("private", "=", true),
        eb.or(
          accountIsAdmin(auth, eb),
          accountIsOwner(auth, eb),
          accountIsRepositoryMember(auth, eb),
          accountIsOrganizationMember(auth, eb),
        ),
      ),
    ),
  ),
)

const canAccessChannel = definePermission<"channel">((auth, eb) =>
  eb.and(
    eb.exists("repository", (q) =>
      q.where((eb) => canAccessRepository(auth, eb)),
    ),
    eb.or(
      eb.cmp("private", "=", false),
      eb.and(
        eb.cmp("private", "=", true),
        eb.or(
          eb.or(
            accountIsAdmin(auth, eb),
            eb.exists("repository", (q) =>
              q.where((eb) => accountIsOwner(auth, eb)),
            ),
            eb.and(
              eb.exists("members", (q) =>
                q.where("account_id", "=", auth?.id ?? ""),
              ),
              eb.exists("permissions", (q) =>
                q
                  .where("scope", "=", "channel")
                  .where("permission_id", "=", "channel.view"),
              ),
            ),
            eb.and(
              eb.exists("repository", (q) =>
                q.where((eb) => accountIsOrganizationMember(auth, eb)),
              ),
              eb.exists("permissions", (q) =>
                q
                  .where("scope", "=", "organization")
                  .where("permission_id", "=", "channel.view"),
              ),
            ),
            eb.and(
              eb.exists("repository", (q) =>
                q.where((eb) => accountIsRepositoryMember(auth, eb)),
              ),
              eb.exists("permissions", (q) =>
                q
                  .where("scope", "=", "repository")
                  .where("permission_id", "=", "channel.view"),
              ),
            ),
          ),
        ),
      ),
    ),
  ),
)

// const canAccessInstallation = definePermission<"installation">((auth, eb) =>
//   eb.and(
//     installationIsActive(auth, eb),
//     eb.or(
//       accountIsAdmin(auth, eb),
//       accountIsOwner(auth, eb),
//       accountIsOrganizationMember(auth, eb),
//     ),
//   ),
// )

const canAccessMessage = definePermission<"message">((auth, eb) =>
  eb.exists("channel", (q) => q.where((eb) => canAccessChannel(auth, eb))),
)
